import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CustomSelect from '../forms/theme-elements/CustomSelect';
import { capitalize } from 'lodash';
import { getCallsFiltersByCampaignType } from '@pages/Admin/Campaign/ui/utils';
import { IconChevronDown, IconRefresh } from '@tabler/icons-react';
import { useStyles } from './styles';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { CallsFilterFormValue } from '@features/Admin/Campaign/model/type';
import { CallStatus, CallType, CampaignDto } from '@shared/services/apiService';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { CallEndedReasons, CallStatuses } from './consts';
import { CallEndedReasonsType } from '@shared/services/apiService/missing-types';
import { t } from 'i18next';
import { useState, useEffect } from 'react';

interface CallsFilterProps {
  campaignData: CampaignDto;
  formMethods: UseFormReturn<CallsFilterFormValue>;
  isExtendedSearch: boolean;
  onExtendedSearchChange: (value: boolean) => void;
}

export const CallsFilter: React.FC<CallsFilterProps> = ({
  campaignData,
  formMethods,
  isExtendedSearch,
  onExtendedSearchChange,
}: CallsFilterProps) => {
  const theme = useTheme();
  const { classes } = useStyles();

  const { control, reset, setValue, watch } = formMethods;

  const { statuses, types, endedReason, start, end, numberSearch, callIdSearch } = watch();
  const [searchText, setSearchText] = useState(numberSearch || '');
  const [callIdSearchText, setCallIdSearchText] = useState(callIdSearch || '');

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchText !== numberSearch) {
        setValue('numberSearch', searchText);
      }
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText, numberSearch, setValue]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (callIdSearchText !== callIdSearch) {
        setValue('callIdSearch', callIdSearchText);
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [callIdSearchText, callIdSearch, setValue]);

  const handleDate = (key: keyof CallsFilterFormValue, date: Dayjs | null): void => {
    if (date) {
      setValue(key, date.toISOString());
    } else {
      setValue(key, null);
    }
  };

  const handleStatus = (status: CallStatus): void => {
    setValue('statuses', status);
  };

  const handleCallType = (type: CallType): void => {
    setValue('types', type);
  };

  const handleEndedReason = (reason: CallEndedReasonsType): void => {
    setValue('endedReason', reason);
  };

  // const handleSentimentIndicator = (sentimentIndicator: SentimentType): void => {
  //   setValue('sentimentIndicator', sentimentIndicator);
  // };

  const parseDate = (date: string | null | undefined) => {
    if (date) {
      return dayjs(date);
    }
    return null;
  };

  return (
    <FormProvider {...formMethods}>
      <Box
        gap={1.5}
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}>
        {/* Row 1: Text/select filters */}
        <Box
          gap={1.5}
          sx={{
            display: 'flex',
            flexDirection: 'row',
          }}>
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <Controller
              name="numberSearch"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  id="number-search-input"
                  label={t('campaignPage.ongoingCallsTable.filter.search')}
                  placeholder={t('campaignPage.ongoingCallsTable.filter.searchPlaceholder')}
                  size="small"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              )}
            />
          </Box>

          <FormControl
            sx={{ flex: '1 1 0', minWidth: 0 }}
            variant="outlined"
            size="small"
            className={classes.filter_select}>
            <InputLabel id="callType-label" shrink={!!types}>
              {t('campaignPage.ongoingCallsTable.filter.type')}
            </InputLabel>
            <CustomSelect
              id="callType-select"
              labelId="callType-label"
              input={
                <OutlinedInput
                  label={t('campaignPage.ongoingCallsTable.filter.type')}
                  notched={!!types}
                />
              }
              value={types ?? ''}
              name="types"
              displayEmpty
              IconComponent={IconChevronDown}
              onChange={(e: SelectChangeEvent) => handleCallType(e.target.value as CallType)}
              renderValue={(value: string) =>
                value ? (
                  capitalize(value).replaceAll('-', ' ')
                ) : (
                  <Typography color={theme.palette.extendedGrey?.neutral}>
                    {t('campaignPage.ongoingCallsTable.filter.type')}
                  </Typography>
                )
              }>
              {getCallsFiltersByCampaignType(campaignData.type)?.map((filter) => (
                <MenuItem key={filter} value={filter}>
                  {capitalize(filter).replaceAll('-', ' ')}
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl>

          <FormControl
            sx={{ flex: '1 1 0', minWidth: 0 }}
            variant="outlined"
            size="small"
            className={classes.filter_select}>
            <InputLabel id="callStatus-label" shrink={!!statuses}>
              {t('campaignPage.ongoingCallsTable.filter.status')}
            </InputLabel>
            <CustomSelect
              id="callStatus-select"
              labelId="callStatus-label"
              input={
                <OutlinedInput
                  label={t('campaignPage.ongoingCallsTable.filter.status')}
                  notched={!!statuses}
                />
              }
              value={statuses ?? ''}
              name="statuses"
              displayEmpty
              IconComponent={IconChevronDown}
              onChange={(e: SelectChangeEvent) => handleStatus(e.target.value as CallStatus)}
              renderValue={(value: string) =>
                value ? (
                  capitalize(value).replaceAll('-', ' ')
                ) : (
                  <Typography color={theme.palette.extendedGrey?.neutral}>
                    {t('campaignPage.ongoingCallsTable.filter.status')}
                  </Typography>
                )
              }>
              {Object.values(CallStatuses)?.map((filter) => (
                <MenuItem key={filter} value={filter}>
                  {capitalize(filter)}
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl>

          <FormControl
            sx={{ flex: '1 1 0', minWidth: 0 }}
            variant="outlined"
            size="small"
            className={classes.filter_select}>
            <InputLabel id="endedReason-label" shrink={!!endedReason}>
              {t('campaignPage.ongoingCallsTable.filter.endReason')}
            </InputLabel>
            <CustomSelect
              id="endedReason-select"
              labelId="endedReason-label"
              input={
                <OutlinedInput
                  label={t('campaignPage.ongoingCallsTable.filter.endReason')}
                  notched={!!endedReason}
                />
              }
              value={endedReason ?? ''}
              name="endedReason"
              displayEmpty
              IconComponent={IconChevronDown}
              onChange={(e: SelectChangeEvent) =>
                handleEndedReason(e.target.value as CallEndedReasonsType)
              }
              renderValue={(value: string) =>
                value ? (
                  capitalize(value).replaceAll('-', ' ')
                ) : (
                  <Typography color={theme.palette.extendedGrey?.neutral}>
                    {t('campaignPage.ongoingCallsTable.filter.endReason')}
                  </Typography>
                )
              }>
              {Object.values(CallEndedReasons)?.map((filter) => (
                <MenuItem key={filter} value={filter}>
                  {capitalize(filter).replaceAll('-', ' ')}
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl>
        </Box>

        {/* Row 2: Date range + reset */}
        <Box
          gap={1.5}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
          }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
            <DemoContainer
              components={['DateTimePicker']}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                '> .MuiTextField-root': { minWidth: 'unset !important' },
                overflow: 'clip',
              }}>
              <DesktopDateTimePicker
                label={t('campaignPage.ongoingCallsTable.startDate')}
                value={parseDate(start)}
                sx={{ width: '100%' }}
                format="DD/MM/YYYY hh:mm A"
                onChange={(newValue) => {
                  handleDate('start', newValue);
                }}
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: {
                      placeholder: 'DD/MM/YYYY hh:mm aa',
                    },
                    sx: {
                      '& .MuiOutlinedInput-input': {
                        paddingRight: 0,
                      },
                    },
                  },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
            <DemoContainer
              components={['DateTimePicker']}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                '> .MuiTextField-root': { minWidth: 'unset !important' },
                overflow: 'clip',
              }}>
              <DesktopDateTimePicker
                label={t('campaignPage.ongoingCallsTable.endDate')}
                value={parseDate(end)}
                sx={{ width: '100%' }}
                format="DD/MM/YYYY hh:mm A"
                onChange={(newValue) => {
                  handleDate('end', newValue);
                }}
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: {
                      placeholder: 'DD/MM/YYYY hh:mm aa',
                    },
                    sx: {
                      '& .MuiOutlinedInput-input': {
                        paddingRight: 0,
                      },
                    },
                  },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>

          {/* TODO: uncomment when refactor sentiments logic will be done */}
          {/* <FormControl sx={{ flex: '1 1 0', minWidth: 0 }} variant="outlined" size="small" className={classes.filter_select}>
            <InputLabel id="sentimentIndicator-label">
              {t('campaignPage.ongoingCallsTable.filter.sentiment')}
            </InputLabel>
            <CustomSelect
              id="sentimentIndicator-select"
              labelId="sentimentIndicator-label"
              input={<OutlinedInput label={t('campaignPage.ongoingCallsTable.filter.sentiment')} />}
              value={sentimentIndicator ?? ''}
              name="sentimentIndicator"
              displayEmpty
              IconComponent={IconChevronDown}
              onChange={(e: SelectChangeEvent) =>
                handleSentimentIndicator(e.target.value as SentimentType)
              }
              renderValue={(value: string) =>
                value ? (
                  capitalize(value).replaceAll('-', ' ')
                ) : (
                  <Typography color={theme.palette.extendedGrey?.neutral}>
                    {t('campaignPage.ongoingCallsTable.filter.sentiment')}
                  </Typography>
                )
              }>
              {Object.values(SentimentIndicators).map((filter) => (
                <MenuItem key={filter} value={filter}>
                  {capitalize(filter).replaceAll('-', ' ')}
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl> */}

          <Button
            onClick={() => {
              reset();
              setSearchText('');
              setCallIdSearchText('');
            }}
            size="small"
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.5,
              }}>
              <IconRefresh /> {t('campaignPage.ongoingCallsTable.filter.reset')}
            </Box>
          </Button>
        </Box>

        <Box
          gap={1.5}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isExtendedSearch}
                onChange={(e) => onExtendedSearchChange(e.target.checked)}
              />
            }
            label={t('campaignPage.ongoingCallsTable.filter.extendedSearch')}
          />
          {isExtendedSearch && (
            <Controller
              name="callIdSearch"
              control={control}
              render={() => (
                <TextField
                  fullWidth
                  id="call-id-search-input"
                  placeholder={t('campaignPage.ongoingCallsTable.filter.callIdSearchPlaceholder')}
                  size="small"
                  value={callIdSearchText}
                  onChange={(e) => setCallIdSearchText(e.target.value)}
                  sx={{ flex: '1 1 0', minWidth: 0 }}
                />
              )}
            />
          )}
        </Box>
      </Box>
    </FormProvider>
  );
};
